import type { components } from '@/api/schema'
import { ipoApi } from '@/features/ipo/api/ipoApi'
import { pageTotal } from '@/features/market/api/marketMappers'
import { formatRupees } from '@/lib/broker-format'
import { formatBsDate, shortBsLabel, toBsDate } from '@/lib/bsDate'
import { formatMoney, formatQuantity } from '@/lib/format'
import { isZero, money, mul } from '@/lib/money'
import {
  ipoListPageSchema,
  issueDetailSchema,
  issuePeerSchema,
  issuesPageSchema,
  type IpoDetailRow,
  type IpoFact,
  type IpoListPage,
  type IpoStep,
  type IssueDetail,
  type IssuePeer,
  type IssueQuery,
  type IssuesPage,
} from '@/schemas/ipo'

/**
 * The seam between the IPO pages and mm-be's `/api/v5/public-issues/`.
 *
 * Wire rows are mapped to the screen's shape here and branded by the schema
 * parse on the way out, so a contract change fails at the first call rather
 * than in a component. The detail page's figures that mm-be keeps no data for
 * — oversubscription, odds, the allotment date — are not invented here; the
 * screen draws what exists.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]
type WireCard = Wire<'PublicIssueCard'>
type WireDetail = Wire<'PublicIssueDetail'>

const AD_DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

/** en-CA formats as YYYY-MM-DD, so today compares directly with the API's dates. */
const KATHMANDU_ISO_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kathmandu' })

/** A BS display date, or null when there is no date or the converter's table does not reach it. */
const bsDate = (iso: string | null): string | null =>
  toBsDate(iso) === null ? null : formatBsDate(iso)

/** "Bhadra 5": the mock's short date (this BS year drops its year), or null with no date. */
const shortDate = (iso: string | null): string | null => {
  const full = bsDate(iso)
  return full === null ? null : shortBsLabel(full)
}

/** "15 Sep 2026", printed under a BS date for members who plan in AD. */
const adDate = (iso: string): string => AD_DATE.format(new Date(`${iso}T00:00:00Z`))

const DAY_MS = 86_400_000

/** Whole days between two plain ISO dates, both read as UTC midnight so no DST shortens one. */
const dayGap = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS)

/**
 * Whole days from today to the close, today NOT counted (the revamp mock's rule, owner P1
 * 2026-10-29): an issue closing in three days has "3 days left", one closing tomorrow has one, and
 * the close day itself is the last day, 0.
 */
const remainingDays = (today: string, closes: string): number => dayGap(today, closes)

const untilClose = (days: number): string =>
  days <= 0 ? 'closes today' : days === 1 ? 'closes tomorrow' : `closes in ${String(days)} days`

const daysLeft = (days: number): string =>
  days <= 0 ? 'Last day' : days === 1 ? '1 day left' : `${String(days)} days left`

/** Legacy rows store blanks as empty strings as often as nulls. */
const orNull = (value: string | null): string | null =>
  value === null || value.trim() === '' ? null : value

const rupees = (value: string): string => `Rs ${formatMoney(money(value))}`

const sentenceWord = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

/** Funds and bonds are sold in units; shares in kitta. */
const unitWord = (type: string): 'kitta' | 'units' =>
  type === 'mutual_fund' || type === 'bonds' ? 'units' : 'kitta'

const perUnit = (type: string): string => (unitWord(type) === 'kitta' ? 'kitta' : 'unit')

/** Legacy prospectus values are sometimes a bare file name; only a real link is a link. */
const prospectusUrl = (value: string | null): string | null =>
  value !== null && /^https?:\/\//i.test(value) ? value : null

const toIssueRow = (wire: WireCard) => ({
  key: wire.key,
  type: wire.type,
  typeLabel: wire.type_label,
  symbol: orNull(wire.symbol),
  company: wire.company_name,
  category: orNull(wire.category),
  sector: orNull(wire.sector),
  price: wire.price,
  priceLabel: wire.price_label,
  pricingBadge: orNull(wire.pricing_badge),
  units: wire.units,
  ratio: orNull(wire.ratio),
  shareType: orNull(wire.share_type),
  opens: bsDate(wire.opens),
  closes: bsDate(wire.closes),
  status: wire.status,
  prospectus: prospectusUrl(orNull(wire.prospectus)),
})

/** The close that actually binds: an extended final close beats the original. */
const closingDate = (wire: WireDetail): string | null => wire.final_close_date ?? wire.closes

/**
 * "Rs 100 per kitta · minimum 10 kitta · closes in 3 days".
 *
 * The close is relative while the issue is open, because the only thing this
 * line is asked for is how long is left; the strip under it carries the date.
 */
const priceLine = (wire: WireDetail, today: string): string => {
  const price =
    wire.price === null
      ? `${wire.price_label} not set yet`
      : `${rupees(wire.price)} per ${perUnit(wire.type)}`
  const minimum =
    wire.min_kitta === null
      ? null
      : `minimum ${formatQuantity(wire.min_kitta)} ${unitWord(wire.type)}`
  const opens = shortDate(wire.opens)
  const closesIso = closingDate(wire)
  const closes = shortDate(closesIso)
  const when =
    wire.status === 'open'
      ? closesIso === null
        ? 'open now'
        : untilClose(remainingDays(today, closesIso))
      : wire.status === 'upcoming'
        ? opens === null
          ? 'dates not announced'
          : `opens ${opens}`
        : closes === null
          ? 'closed'
          : `closed ${closes}`

  return [price, minimum, when].filter((part) => part !== null).join(' · ')
}

/** "1:4" → "1 new share per 4 held": what a right entitles a holder to, as the mock spells it. */
const entitlement = (ratio: string): string => {
  const [new_, held] = ratio.split(':').map((part) => part.trim())
  if (new_ === undefined || held === undefined || new_ === '' || held === '') return ratio
  return `${new_} new share${new_ === '1' ? '' : 's'} per ${held} held`
}

/**
 * The third tile: what you may apply for.
 *
 * An IPO or FPO carries a band ("10 to 1,000 kitta") over what the smallest application costs. A
 * right has none (the ratio and your holding decide the size) and an auction none (you set the
 * bid), so each says what it is instead. Anything else drops to the opening date.
 */
const applyTile = (wire: WireDetail): IpoFact | null => {
  const min = wire.min_kitta
  const ratio = orNull(wire.ratio)
  const shareType = orNull(wire.share_type)
  const unit = unitWord(wire.type)

  if (min === null) {
    if (wire.type === 'right' && ratio !== null)
      return { label: 'Apply', value: `${ratio} ratio`, hint: entitlement(ratio) }
    if (wire.type === 'auction')
      return {
        label: 'Apply',
        value: 'On auction',
        hint: [shareType === null ? null : `${sentenceWord(shareType)} shares`, orNull(wire.symbol)]
          .filter((part) => part !== null)
          .join(', '),
      }
    return null
  }

  return {
    label: 'Apply',
    value:
      wire.max_kitta === null
        ? `${formatQuantity(min)} ${unit} and up`
        : `${formatQuantity(min)} to ${formatQuantity(wire.max_kitta)} ${unit}`,
    hint:
      wire.price === null
        ? `${formatQuantity(min)} ${unit} minimum`
        : `${rupees(mul(money(wire.price), min))} minimum`,
  }
}

/**
 * Issue size, price, the band you may apply in, and the deadline.
 *
 * The opening date takes the third tile when the issue has no band. The
 * deadline stays last because `IpoFactsGrid` paints the last tile as the one
 * with a clock on it.
 */
const facts = (wire: WireDetail, today: string): IpoFact[] => {
  const count = wire.units ?? wire.issue_size_number
  const opens = wire.opens
  const closes = closingDate(wire)
  const extended =
    wire.final_close_date !== null && wire.closes !== null && wire.final_close_date !== wire.closes

  return [
    {
      label: 'Issue size',
      value: count === null ? 'Not published' : `${formatQuantity(count)} ${unitWord(wire.type)}`,
      hint:
        wire.issue_size_amount === null
          ? wire.type_label
          : formatRupees(money(wire.issue_size_amount)),
    },
    {
      label: wire.price_label,
      value: wire.price === null ? 'Not set yet' : rupees(wire.price),
      hint:
        wire.pricing_badge === null
          ? `Per ${perUnit(wire.type)}`
          : `Per ${perUnit(wire.type)}, ${wire.pricing_badge.toLowerCase()}`,
    },
    applyTile(wire) ?? {
      label: 'Opens',
      value: shortDate(opens) ?? 'Not announced',
      hint: opens === null ? 'Date not set yet' : adDate(opens),
    },
    {
      label: 'Closes',
      value: shortDate(closes) ?? 'Not announced',
      hint:
        closes === null
          ? 'Date not set yet'
          : extended && wire.closes !== null
            ? `Extended from ${shortDate(wire.closes) ?? adDate(wire.closes)}`
            : wire.status === 'open'
              ? daysLeft(remainingDays(today, closes))
              : adDate(closes),
    },
  ]
}

interface Milestone {
  iso: string | null
  sublabel: string
}

/**
 * Every dated step the issue has, oldest first. The next one on or after today
 * is `current`; anything before it is done.
 */
const timeline = (wire: WireDetail, today: string): IpoStep[] => {
  const extendedClose = wire.final_close_date === wire.closes ? null : wire.final_close_date
  const milestones: Milestone[] = [
    { iso: wire.approved_date, sublabel: 'Approved by SEBON' },
    { iso: wire.book_closure_date, sublabel: 'Book closes, holders fixed' },
    { iso: wire.opens, sublabel: 'Opens' },
    { iso: wire.closes, sublabel: 'Closes' },
    { iso: extendedClose, sublabel: 'Extended close' },
    { iso: wire.bid_opening_date, sublabel: 'Bids opened' },
    { iso: wire.listing_date, sublabel: 'Listed on NEPSE' },
  ]
  const dated = milestones
    .flatMap((m) => (m.iso === null ? [] : [{ iso: m.iso, sublabel: m.sublabel }]))
    .sort((a, b) => a.iso.localeCompare(b.iso))
  const next = dated.findIndex((m) => m.iso >= today)

  return dated.map((m, i) => ({
    label: shortDate(m.iso) ?? adDate(m.iso),
    sublabel: m.sublabel,
    state: next === -1 || i < next ? 'done' : i === next ? 'current' : 'upcoming',
  }))
}

/**
 * The primary button's words for the window the issue is in (the mock's `pi-apply`): the live
 * action while it is open, otherwise what it is waiting for. Only an open issue is actionable.
 */
const applyLabel = (wire: WireDetail): string => {
  if (wire.status === 'open')
    return wire.type === 'auction' ? 'Bid on MeroShare' : 'Apply on MeroShare'
  if (wire.status === 'closed') return 'Applications closed'
  const opens = shortDate(wire.opens)
  return opens === null ? 'Dates not fixed' : `Opens ${opens}`
}

const details = (wire: WireDetail): IpoDetailRow[] => {
  const category = orNull(wire.category)
  const symbol = orNull(wire.symbol)
  const rows: (IpoDetailRow | null)[] = [
    {
      label: 'Type',
      value: category === null ? wire.type_label : `${wire.type_label} · ${category}`,
      tone: 'neutral',
    },
    symbol === null ? null : { label: 'Symbol', value: symbol, tone: 'neutral' },
    wire.sector === null ? null : { label: 'Sector', value: wire.sector, tone: 'neutral' },
    wire.face_value === null
      ? null
      : { label: 'Face value', value: rupees(wire.face_value), tone: 'neutral' },
    wire.premium_value === null || isZero(money(wire.premium_value))
      ? null
      : { label: 'Premium', value: rupees(wire.premium_value), tone: 'accent' },
    wire.ratio === null ? null : { label: 'Ratio', value: wire.ratio, tone: 'neutral' },
    wire.share_type === null
      ? null
      : {
          label: 'Share type',
          value: wire.share_type.charAt(0).toUpperCase() + wire.share_type.slice(1),
          tone: 'neutral',
        },
  ]
  return rows.filter((row) => row !== null)
}

/** One "Load more issues" worth. */
const IPO_LIST_PAGE_SIZE = 5

interface ListCursor {
  status: 'open' | 'upcoming'
  page: number
  /** Open plus upcoming, counted on the first page and carried forward. */
  total: number
}

const encodeCursor = (cursor: ListCursor): string =>
  `${cursor.status}.${String(cursor.page)}.${String(cursor.total)}`

const decodeCursor = (raw: string): ListCursor => {
  const [status, page, total] = raw.split('.')
  return {
    status: status === 'upcoming' ? 'upcoming' : 'open',
    page: Number(page),
    total: Number(total),
  }
}

/**
 * The Current tab reads both windows in one read each at the endpoint's ceiling (`max_value=200`,
 * mm-be `IssueListRequestSerializer`): open and upcoming issues are few, so the two lists merge
 * and page on the client without the pager's total lying.
 */
const CURRENT_PAGE_SIZE = 200

export const ipoService = {
  /**
   * The mock's Current tab: every open issue, then every upcoming one, each in the server's order.
   * `status=open` and `status=upcoming` are separate calls (mm-be's `status` takes one value), made
   * together; the merged list is paged here.
   */
  currentIssues: async (query: Omit<IssueQuery, 'status'>): Promise<IssuesPage> => {
    const read = (status: 'open' | 'upcoming') =>
      ipoApi.issues({
        page: 1,
        page_size: CURRENT_PAGE_SIZE,
        status,
        ...(query.type === undefined ? {} : { type: query.type }),
      })
    const [open, upcoming] = await Promise.all([read('open'), read('upcoming')])
    const rows = [...open.rows, ...upcoming.rows].map(toIssueRow)
    const from = (query.page - 1) * query.pageSize

    return issuesPageSchema.parse({
      rows: rows.slice(from, from + query.pageSize),
      page: query.page,
      pageSize: query.pageSize,
      total: rows.length,
    })
  },

  issues: async (query: IssueQuery): Promise<IssuesPage> => {
    const page = await ipoApi.issues({
      page: query.page,
      page_size: query.pageSize,
      status: query.status,
      ...(query.type === undefined ? {} : { type: query.type }),
    })

    return issuesPageSchema.parse({
      rows: page.rows.map(toIssueRow),
      page: query.page,
      pageSize: query.pageSize,
      total: pageTotal(page.meta, page.rows.length),
    })
  },

  /** `now` is injected so the timeline's done/current split is testable. */
  issue: async (key: string, now: Date = new Date()): Promise<IssueDetail> => {
    const wire = await ipoApi.issue(key)
    const today = KATHMANDU_ISO_DATE.format(now)

    return issueDetailSchema.parse({
      key: wire.key,
      type: wire.type,
      typeLabel: wire.type_label,
      status: wire.status,
      company: wire.company_name,
      symbol: orNull(wire.symbol),
      sector: orNull(wire.sector),
      priceLine: priceLine(wire, today),
      applyLabel: applyLabel(wire),
      opens: shortDate(wire.opens),
      closes: shortDate(closingDate(wire)),
      facts: facts(wire, today),
      timeline: timeline(wire, today),
      details: details(wire),
      prospectus: prospectusUrl(orNull(wire.prospectus)),
    })
  },

  /** mm-be only returns peers that are already trading, but a null price is still skipped here. */
  peers: async (key: string): Promise<IssuePeer[]> => {
    const page = await ipoApi.peers(key)

    return issuePeerSchema.array().parse(
      page.rows
        .filter((peer) => peer.last_price !== null)
        .map((peer) => ({
          symbol: orNull(peer.symbol) ?? peer.name,
          issuePrice: peer.issue_price,
          lastPrice: peer.last_price,
          gainPercent: peer.gain_percent,
        })),
    )
  },

  /**
   * Open issues first, then upcoming ones, behind "Load more issues".
   *
   * The cursor is opaque to the caller; a real keyset cursor later changes
   * nothing above this module.
   */
  // TODO(tech-debt): mm-be's `status` filter takes one value, so this walks the
  // open pages and then the upcoming ones, and the first page spends a second
  // call on the upcoming count. Collapse to one call once the backend accepts
  // `status=open,upcoming`.
  ipoList: async (cursor?: string): Promise<IpoListPage> => {
    const at = cursor === undefined ? null : decodeCursor(cursor)
    const status = at?.status ?? 'open'
    const pageNumber = at?.page ?? 1

    const [page, upcoming] = await Promise.all([
      ipoApi.issues({ status, page: pageNumber, page_size: IPO_LIST_PAGE_SIZE }),
      at === null ? ipoApi.issues({ status: 'upcoming', page: 1, page_size: 1 }) : null,
    ])
    const statusTotal = pageTotal(page.meta, page.rows.length)
    const total =
      at?.total ??
      statusTotal + (upcoming === null ? 0 : pageTotal(upcoming.meta, upcoming.rows.length))

    const next: ListCursor | null =
      pageNumber * IPO_LIST_PAGE_SIZE < statusTotal
        ? { status, page: pageNumber + 1, total }
        : status === 'open' && total > statusTotal
          ? { status: 'upcoming', page: 1, total }
          : null

    return ipoListPageSchema.parse({
      rows: page.rows.map(toIssueRow),
      ...(next === null ? {} : { nextCursor: encodeCursor(next) }),
      total,
    })
  },
}
